import { createElement, useEffect, useRef, useState, type DragEvent as ReactDragEvent } from 'react';

import {
  SidebarChevronIcon,
  SidebarDatabaseIcon,
  SidebarMoreIcon,
  SidebarPageBlankIcon,
  SidebarPageWithChildrenIcon,
  SidebarPlusIcon,
} from './SidebarIcons.web';
import { useFavoriteBlock } from '@/lib/favorites/use-favorite-block';
import { useLlmPrivateBlock } from '@/lib/llm-private/use-llm-private-block';
import { copyPageAsMarkdown, downloadPageAsMarkdown } from '@/lib/blocks/page-export';

import { SidebarContextMenu } from './SidebarContextMenu';

type SidebarItemProps = {
  label: string;
  depth: number;
  isActive: boolean;
  isExpanded?: boolean;
  hasChildPages?: boolean;
  leadingIcon?: 'page' | 'database';
  canDuplicate?: boolean;
  canTrash?: boolean;
  canRename?: boolean;
  canFavorite?: boolean;
  variant?: 'section' | 'page';
  /** Section header that only expands/collapses (Recents, Favorites), not a page link. */
  headerToggleOnly?: boolean;
  blockId?: string;
  draggable?: boolean;
  isDragging?: boolean;
  dropHint?: 'before' | 'inside' | null;
  onDragStart?: (event: ReactDragEvent<HTMLDivElement>) => void;
  onDragEnd?: () => void;
  onDragOver?: (event: ReactDragEvent<HTMLDivElement>) => void;
  onDragLeave?: () => void;
  onDrop?: (event: ReactDragEvent<HTMLDivElement>) => void;
  onPress: () => void;
  onPrefetch?: () => void;
  onPrefetchChildren?: () => void;
  onToggleExpand?: () => void;
  onNewSubpage?: () => void;
  onDuplicate?: () => void;
  onRename?: () => void;
  onTrash?: () => void;
};

type MenuPoint = { x: number; y: number };

const INDENT_PX = 12;
const LEADING_SIZE = 18;
const ACTION_SLOT = 22;

export function SidebarItem({
  label,
  depth,
  isActive,
  isExpanded,
  hasChildPages = false,
  leadingIcon = 'page',
  canDuplicate = true,
  canTrash = true,
  canRename = true,
  canFavorite = true,
  variant = 'page',
  headerToggleOnly = false,
  blockId,
  draggable: isDraggable = false,
  isDragging = false,
  dropHint = null,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onPress,
  onPrefetch,
  onPrefetchChildren,
  onToggleExpand,
  onNewSubpage,
  onDuplicate,
  onRename,
  onTrash,
}: SidebarItemProps) {
  const favorite = useFavoriteBlock(canFavorite ? blockId : null);
  const llmPrivate = useLlmPrivateBlock(canFavorite ? blockId : null);
  const rowRef = useRef<HTMLDivElement>(null);
  const prefetchedChildrenRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPoint, setMenuPoint] = useState<MenuPoint>({ x: 0, y: 0 });
  const paddingLeft = 4 + depth * INDENT_PX;
  const hasMenu = Boolean(onDuplicate && onRename && onTrash);
  const isSection = variant === 'section';
  const canExpand = Boolean(hasChildPages && onToggleExpand);
  const sectionCanExpand = Boolean(onToggleExpand);
  const labelAction = headerToggleOnly && onToggleExpand ? onToggleExpand : onPress;
  const showActions = hasMenu || Boolean(onNewSubpage);
  const actionsWidth = showActions ? (hasMenu && onNewSubpage ? ACTION_SLOT * 2 : ACTION_SLOT) : 0;
  const rowClass = [
    'sidebar-row group mb-0 flex min-h-[28px] w-full flex-row items-stretch rounded-md py-0 pr-0.5 select-none',
    variant === 'section' ? 'is-section' : '',
    isActive ? 'is-active' : '',
    menuOpen ? 'is-menu-open' : '',
    !isSection && canExpand ? 'has-expand' : '',
    isDragging ? 'is-dragging' : '',
    dropHint === 'inside' ? 'is-drop-inside' : '',
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    if (!hasMenu) {
      return;
    }
    const node = rowRef.current;
    if (!node) {
      return;
    }
    function onContextMenu(event: MouseEvent): void {
      event.preventDefault();
      openMenuAt({ x: event.clientX, y: event.clientY });
    }
    node.addEventListener('contextmenu', onContextMenu);
    return () => node.removeEventListener('contextmenu', onContextMenu);
  }, [hasMenu]);

  function openMenuAt(point: MenuPoint): void {
    setMenuPoint(point);
    setMenuOpen(true);
  }

  function onRowMouseEnter(): void {
    onPrefetch?.();
    if (!prefetchedChildrenRef.current && onPrefetchChildren) {
      prefetchedChildrenRef.current = true;
      onPrefetchChildren();
    }
  }

  function onCopyMarkdownExport(): void {
    if (!blockId) {
      return;
    }
    void copyPageAsMarkdown(blockId).catch((err: unknown) => {
      console.error(err);
    });
  }

  function onDownloadMarkdownExport(): void {
    if (!blockId) {
      return;
    }
    void downloadPageAsMarkdown(blockId).catch((err: unknown) => {
      console.error(err);
    });
  }

  const canExportMarkdown = leadingIcon === 'page' && Boolean(blockId);

  return createElement(
    'div',
    { className: 'sidebar-row-wrap relative' },
    dropHint === 'before'
      ? createElement('div', { className: 'sidebar-drop-indicator sidebar-drop-indicator-before' })
      : null,
    createElement(
      'div',
      {
        ref: rowRef,
        className: rowClass,
        style: { paddingLeft },
        draggable: isDraggable,
        'data-sidebar-block-id': blockId,
        onMouseEnter: onRowMouseEnter,
        onClick: isDraggable
          ? (event: MouseEvent) => {
              const target = event.target as HTMLElement;
              if (target.closest('button')) {
                return;
              }
              onPress();
            }
          : undefined,
        onDragStart: isDraggable
          ? (event: ReactDragEvent<HTMLDivElement>) => {
              const transfer = event.dataTransfer;
              if (!transfer) {
                return;
              }
              transfer.effectAllowed = 'move';
              if (blockId) {
                transfer.setData('text/plain', blockId);
              }
              onDragStart?.(event);
            }
          : undefined,
        onDragEnd: isDraggable ? () => onDragEnd?.() : undefined,
        onDragOver: onDragOver
          ? (event: ReactDragEvent<HTMLDivElement>) => {
              event.preventDefault();
              const transfer = event.dataTransfer;
              if (transfer) {
                transfer.dropEffect = 'move';
              }
              onDragOver(event);
            }
          : undefined,
        onDragLeave: onDragLeave,
        onDrop: onDrop
          ? (event: ReactDragEvent<HTMLDivElement>) => {
              event.preventDefault();
              event.stopPropagation();
              onDrop(event);
            }
          : undefined,
      },
      ...(isSection
        ? [
            createElement(
              'div',
              {
                role: 'button',
                tabIndex: 0,
                className:
                  'sidebar-label-hit sidebar-section-label-hit flex min-w-0 flex-1 items-center py-0 text-left',
                onClick: labelAction,
                onKeyDown: (event: KeyboardEvent) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    labelAction();
                  }
                },
              },
              createElement('span', { className: 'sidebar-label-section truncate' }, label),
            ),
            showActions
              ? createElement(
                  'div',
                  {
                    className: 'flex shrink-0 items-center justify-end self-center',
                    style: { width: actionsWidth, minWidth: actionsWidth },
                  },
                  hasMenu
                    ? createElement(
                        'button',
                        {
                          type: 'button',
                          draggable: false,
                          className:
                            'sidebar-action flex h-[22px] w-[22px] items-center justify-center rounded',
                          onClick: (event: MouseEvent) => {
                            event.stopPropagation();
                            openMenuAt({ x: event.clientX, y: event.clientY });
                          },
                        },
                        createElement(SidebarMoreIcon),
                      )
                    : null,
                  onNewSubpage
                    ? createElement(
                        'button',
                        {
                          type: 'button',
                          draggable: false,
                          className:
                            'sidebar-action flex h-[22px] w-[22px] items-center justify-center rounded',
                          onClick: (event: MouseEvent) => {
                            event.stopPropagation();
                            onNewSubpage();
                          },
                        },
                        createElement(SidebarPlusIcon),
                      )
                    : null,
                )
              : null,
            sectionCanExpand
              ? createElement(
                  'button',
                  {
                    type: 'button',
                    draggable: false,
                    className:
                      'sidebar-section-chevron flex h-[22px] w-[22px] items-center justify-center rounded',
                    onClick: (event: MouseEvent) => {
                      event.stopPropagation();
                      onToggleExpand?.();
                    },
                  },
                  createElement(SidebarChevronIcon, { expanded: Boolean(isExpanded) }),
                )
              : null,
          ]
        : [
            createElement(
              'div',
              {
                className: 'relative mr-1 flex shrink-0 items-center self-center',
                style: { width: LEADING_SIZE, height: LEADING_SIZE },
              },
              createElement(
                'div',
                {
                  className:
                    'sidebar-leading-icon pointer-events-none absolute inset-0 flex items-center justify-center',
                },
                leadingIcon === 'database'
                  ? createElement(SidebarDatabaseIcon)
                  : hasChildPages
                    ? createElement(SidebarPageWithChildrenIcon)
                    : createElement(SidebarPageBlankIcon),
              ),
              canExpand
                ? createElement(
                    'button',
                    {
                      type: 'button',
                      draggable: false,
                      className:
                        'sidebar-chevron absolute inset-0 flex items-center justify-center rounded',
                      onClick: (event: MouseEvent) => {
                        event.stopPropagation();
                        onToggleExpand?.();
                      },
                    },
                    createElement(SidebarChevronIcon, { expanded: Boolean(isExpanded) }),
                  )
                : null,
            ),
            createElement(
              'div',
              {
                role: 'button',
                tabIndex: isDraggable ? -1 : 0,
                className:
                  'sidebar-label-hit flex min-w-0 flex-1 items-center truncate py-0 text-left',
                onClick: isDraggable ? undefined : onPress,
                onKeyDown: isDraggable
                  ? undefined
                  : (event: KeyboardEvent) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onPress();
                      }
                    },
              },
              createElement('span', { className: 'sidebar-label truncate' }, label),
            ),
            showActions
              ? createElement(
                  'div',
                  {
                    className: 'flex shrink-0 items-center justify-end self-center',
                    style: { width: actionsWidth, minWidth: actionsWidth },
                  },
                  hasMenu
                    ? createElement(
                        'button',
                        {
                          type: 'button',
                          draggable: false,
                          className:
                            'sidebar-action flex h-[22px] w-[22px] items-center justify-center rounded',
                          onClick: (event: MouseEvent) => {
                            event.stopPropagation();
                            openMenuAt({ x: event.clientX, y: event.clientY });
                          },
                        },
                        createElement(SidebarMoreIcon),
                      )
                    : null,
                  onNewSubpage
                    ? createElement(
                        'button',
                        {
                          type: 'button',
                          draggable: false,
                          className:
                            'sidebar-action flex h-[22px] w-[22px] items-center justify-center rounded',
                          onClick: (event: MouseEvent) => {
                            event.stopPropagation();
                            onNewSubpage();
                          },
                        },
                        createElement(SidebarPlusIcon),
                      )
                    : null,
                )
              : null,
          ]),
    ),
    createElement(SidebarContextMenu, {
      canDuplicate,
      canRename,
      canTrash,
      canExportMarkdown,
      canFavorite: canFavorite && Boolean(blockId),
      isFavorite: favorite.isFavorite,
      isPrivate: llmPrivate.isPrivate,
      onClose: () => setMenuOpen(false),
      onCopyMarkdown: canExportMarkdown ? onCopyMarkdownExport : undefined,
      onDuplicate: () => onDuplicate?.(),
      onExportMarkdown: canExportMarkdown ? onDownloadMarkdownExport : undefined,
      onRename: () => onRename?.(),
      onToggleFavorite: () => void favorite.toggle(),
      onTogglePrivate: () => void llmPrivate.toggle(),
      onTrash: () => onTrash?.(),
      visible: menuOpen,
      x: menuPoint.x,
      y: menuPoint.y,
    }),
  );
}
